import type { Lead } from "./pipelineTypes";

// ─── Types ───────────────────────────────────────────────────────────────────

export type IntakeSource = "api" | "webhook" | "web_form" | "manual";

export type IntakeDisposition =
  | "approved"
  | "approved_with_warning"
  | "duplicate_flagged"
  | "quarantined"
  | "rejected";

export type LeadQuality = "high" | "medium" | "low" | "reject";

export interface ValidationResult {
  field: string;
  status: "valid" | "suspicious" | "invalid" | "missing";
  message: string;
}

export interface DuplicateMatch {
  type: "exact_phone" | "exact_email" | "soft_name_zip" | "same_address" | "recent_submission";
  matchedLeadId: string;
  matchedName: string;
  confidence: number; // 0-100
}

export interface IntakeRecord {
  id: string;
  // Raw payload
  rawPayload: Record<string, any>;
  // Normalized data
  normalizedData: {
    primaryName: string;
    secondaryName: string;
    primaryPhone: string;
    secondaryPhone: string;
    primaryEmail: string;
    secondaryEmail: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    projectType: string;
    leadSource: string;
  };
  // Intake meta
  source: IntakeSource;
  receivedAt: string;
  processedAt: string;
  // Validation
  validationResults: ValidationResult[];
  duplicateMatches: DuplicateMatch[];
  geoResult: "in_area" | "out_of_area" | "unknown";
  projectFitResult: "allowed" | "rejected" | "flagged";
  qualityScore: LeadQuality;
  scoreBreakdown: { factor: string; points: number; max: number }[];
  totalScore: number;
  // Decision
  disposition: IntakeDisposition;
  dispositionReason: string;
  // Review
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;
  // Downstream
  createdLeadId?: string;
  deliveredToPipeline: boolean;
}

// ─── Configuration ───────────────────────────────────────────────────────────

export interface IntegrityConfig {
  approvedZipCodes: string[];
  approvedCities: string[];
  allowedProjectTypes: string[];
  rejectedProjectTypes: string[];
  requiredFields: string[];
  duplicateWindowHours: number;
  scoreThresholds: { high: number; medium: number; low: number };
  sourceWeights: Record<string, number>;
}

export const DEFAULT_INTEGRITY_CONFIG: IntegrityConfig = {
  approvedZipCodes: [
    // Tampa area
    "33601", "33602", "33603", "33604", "33605", "33606", "33607", "33608", "33609",
    "33610", "33611", "33612", "33613", "33614", "33615", "33616", "33617", "33618",
    "33619", "33620", "33621", "33624", "33625", "33626", "33629", "33634", "33635",
    "33647",
    // St Petersburg
    "33701", "33702", "33703", "33704", "33705", "33706", "33707", "33708", "33709",
    "33710", "33711", "33712", "33713", "33714", "33715", "33716",
    // Sarasota / Bradenton
    "34201", "34202", "34203", "34205", "34207", "34208", "34209", "34210", "34211",
    "34212", "34228", "34229", "34230", "34231", "34232", "34233", "34234", "34235",
    "34236", "34237", "34238", "34239", "34240", "34241", "34242", "34243",
    // Venice
    "34275", "34285", "34292", "34293",
    // Port Charlotte / Punta Gorda
    "33948", "33950", "33952", "33953", "33954", "33955", "33980", "33981", "33982", "33983",
    // Fort Myers
    "33901", "33902", "33903", "33905", "33906", "33907", "33908", "33912", "33913",
    "33916", "33917", "33919", "33966",
    // Lakeland
    "33801", "33803", "33805", "33809", "33810", "33811", "33812", "33813", "33815",
  ],
  approvedCities: [
    "Tampa", "St. Petersburg", "St Petersburg", "Sarasota", "Bradenton",
    "Venice", "Port Charlotte", "Punta Gorda", "Fort Myers", "Lakeland",
    "Clearwater", "Largo", "Palm Harbor", "Dunedin", "Safety Harbor",
    "Brandon", "Riverview", "Valrico", "Wesley Chapel", "Land O' Lakes",
    "Cape Coral", "Bonita Springs", "Estero", "Naples",
  ],
  allowedProjectTypes: [
    "Shower Remodel", "Tub to Shower Conversion", "Full Bathroom Remodel",
    "Walk-in Tub Installation", "Window Install", "Door Install", "Flooring",
  ],
  rejectedProjectTypes: [
    "Roofing", "HVAC", "Plumbing", "Electrical", "Landscaping",
    "Painting", "Fence", "Pool", "Solar",
  ],
  requiredFields: ["primaryName", "primaryPhone", "zip", "leadSource"],
  duplicateWindowHours: 72,
  scoreThresholds: { high: 80, medium: 55, low: 30 },
  sourceWeights: {
    "Google Ads": 8,
    "Facebook Ads": 7,
    "Referral": 10,
    "Home Show": 9,
    "Website": 8,
    "TV Ad": 6,
    "Angi": 5,
    "HomeAdvisor": 5,
    "Direct Mail": 4,
    "Other": 3,
  },
};

// ─── Normalization ───────────────────────────────────────────────────────────

export function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return `+1${digits.slice(1)}`;
  if (digits.length === 10) return `+1${digits}`;
  return phone.trim();
}

export function normalizeEmail(email: string | null | undefined): string {
  if (!email) return "";
  return email.trim().toLowerCase();
}

export function normalizeZip(zip: string): string {
  const digits = zip.replace(/\D/g, "");
  return digits.slice(0, 5);
}

export function formatPhoneDisplay(e164: string): string {
  const digits = e164.replace(/\D/g, "");
  const d = digits.length === 11 ? digits.slice(1) : digits;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return e164;
}

// ─── Validation ──────────────────────────────────────────────────────────────

export function validatePhone(phone: string): ValidationResult {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return { field: "phone", status: "missing", message: "Phone number is missing" };
  if (digits.length < 10) return { field: "phone", status: "invalid", message: "Phone number too short" };
  if (digits.length > 11) return { field: "phone", status: "invalid", message: "Phone number too long" };
  if (digits.length === 11 && !digits.startsWith("1"))
    return { field: "phone", status: "suspicious", message: "Non-US country code" };
  return { field: "phone", status: "valid", message: "Valid phone number" };
}

export function validateEmail(email: string): ValidationResult {
  if (!email) return { field: "email", status: "missing", message: "Email is missing" };
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!pattern.test(email))
    return { field: "email", status: "invalid", message: "Invalid email format" };
  const domain = email.split("@")[1];
  const disposable = ["mailinator.com", "tempmail.com", "throwaway.email", "guerrillamail.com"];
  if (disposable.includes(domain))
    return { field: "email", status: "suspicious", message: "Disposable email domain" };
  return { field: "email", status: "valid", message: "Valid email" };
}

export function validateRequiredFields(
  data: Record<string, any>,
  required: string[]
): ValidationResult[] {
  return required.map((field) => {
    const val = data[field];
    if (!val || (typeof val === "string" && !val.trim()))
      return { field, status: "missing" as const, message: `${field} is required` };
    return { field, status: "valid" as const, message: `${field} present` };
  });
}

// ─── Duplicate Detection ─────────────────────────────────────────────────────

export function detectDuplicates(
  normalized: IntakeRecord["normalizedData"],
  existingLeads: Lead[],
  _config: IntegrityConfig
): DuplicateMatch[] {
  const matches: DuplicateMatch[] = [];
  const inPhone = normalizePhone(normalized.primaryPhone);

  for (const lead of existingLeads) {
    const existPhone = normalizePhone(lead.primaryPhone);
    if (inPhone && existPhone && inPhone === existPhone) {
      matches.push({
        type: "exact_phone",
        matchedLeadId: lead.id,
        matchedName: lead.primaryName,
        confidence: 95,
      });
    }
    const inEmail = normalizeEmail(normalized.primaryEmail);
    const existEmail = normalizeEmail(lead.primaryEmail);
    if (inEmail && existEmail && inEmail === existEmail) {
      matches.push({
        type: "exact_email",
        matchedLeadId: lead.id,
        matchedName: lead.primaryName,
        confidence: 90,
      });
    }
    // Soft: name + zip
    if (
      normalized.primaryName && lead.primaryName &&
      normalized.primaryName.toLowerCase() === lead.primaryName.toLowerCase() &&
      normalizeZip(normalized.zip) === normalizeZip(lead.zip)
    ) {
      if (!matches.some((m) => m.matchedLeadId === lead.id))
        matches.push({
          type: "soft_name_zip",
          matchedLeadId: lead.id,
          matchedName: lead.primaryName,
          confidence: 75,
        });
    }
    // Same address
    if (
      normalized.address &&
      lead.address &&
      normalized.address.toLowerCase().replace(/\s+/g, "") ===
        lead.address.toLowerCase().replace(/\s+/g, "") &&
      normalizeZip(normalized.zip) === normalizeZip(lead.zip)
    ) {
      if (!matches.some((m) => m.matchedLeadId === lead.id))
        matches.push({
          type: "same_address",
          matchedLeadId: lead.id,
          matchedName: lead.primaryName,
          confidence: 80,
        });
    }
  }
  return matches;
}

// ─── Geographic Filter ───────────────────────────────────────────────────────

export function checkGeo(
  zip: string,
  city: string,
  config: IntegrityConfig
): "in_area" | "out_of_area" | "unknown" {
  const z = normalizeZip(zip);
  if (!z && !city) return "unknown";
  if (z && config.approvedZipCodes.includes(z)) return "in_area";
  if (city && config.approvedCities.some((c) => c.toLowerCase() === city.toLowerCase()))
    return "in_area";
  if (z) return "out_of_area";
  return "unknown";
}

// ─── Project Fit ─────────────────────────────────────────────────────────────

export function checkProjectFit(
  projectType: string,
  config: IntegrityConfig
): "allowed" | "rejected" | "flagged" {
  if (!projectType) return "flagged";
  const lower = projectType.toLowerCase();
  if (config.allowedProjectTypes.some((t) => t.toLowerCase() === lower)) return "allowed";
  if (config.rejectedProjectTypes.some((t) => t.toLowerCase() === lower)) return "rejected";
  return "flagged";
}

// ─── Scoring ─────────────────────────────────────────────────────────────────

export function scoreIntake(
  validations: ValidationResult[],
  duplicates: DuplicateMatch[],
  geo: "in_area" | "out_of_area" | "unknown",
  projectFit: "allowed" | "rejected" | "flagged",
  source: string,
  config: IntegrityConfig
): { quality: LeadQuality; total: number; breakdown: { factor: string; points: number; max: number }[] } {
  const breakdown: { factor: string; points: number; max: number }[] = [];

  // Phone validity (0-15)
  const phoneV = validations.find((v) => v.field === "phone");
  const phoneScore = phoneV?.status === "valid" ? 15 : phoneV?.status === "suspicious" ? 8 : 0;
  breakdown.push({ factor: "Valid Phone", points: phoneScore, max: 15 });

  // Email validity (0-15)
  const emailV = validations.find((v) => v.field === "email");
  const emailScore = emailV?.status === "valid" ? 15 : emailV?.status === "suspicious" ? 5 : emailV?.status === "missing" ? 3 : 0;
  breakdown.push({ factor: "Valid Email", points: emailScore, max: 15 });

  // Completeness (0-20)
  const required = validations.filter((v) => ["primaryName", "primaryPhone", "zip", "leadSource"].includes(v.field));
  const complete = required.filter((v) => v.status === "valid").length;
  const completenessScore = Math.round((complete / Math.max(required.length, 1)) * 20);
  breakdown.push({ factor: "Data Completeness", points: completenessScore, max: 20 });

  // Geo match (0-20)
  const geoScore = geo === "in_area" ? 20 : geo === "unknown" ? 5 : 0;
  breakdown.push({ factor: "Service Area Match", points: geoScore, max: 20 });

  // Project fit (0-15)
  const fitScore = projectFit === "allowed" ? 15 : projectFit === "flagged" ? 5 : 0;
  breakdown.push({ factor: "Project Fit", points: fitScore, max: 15 });

  // Duplicate status (0-5)
  const dupScore = duplicates.length === 0 ? 5 : 0;
  breakdown.push({ factor: "No Duplicates", points: dupScore, max: 5 });

  // Source trust (0-10)
  const sourceScore = config.sourceWeights[source] || 3;
  breakdown.push({ factor: "Source Trust", points: sourceScore, max: 10 });

  const total = breakdown.reduce((s, b) => s + b.points, 0);
  let quality: LeadQuality;
  if (total >= config.scoreThresholds.high) quality = "high";
  else if (total >= config.scoreThresholds.medium) quality = "medium";
  else if (total >= config.scoreThresholds.low) quality = "low";
  else quality = "reject";

  return { quality, total, breakdown };
}

// ─── Decision Engine ─────────────────────────────────────────────────────────

export function makeDecision(
  validations: ValidationResult[],
  duplicates: DuplicateMatch[],
  geo: "in_area" | "out_of_area" | "unknown",
  projectFit: "allowed" | "rejected" | "flagged",
  quality: LeadQuality
): { disposition: IntakeDisposition; reason: string } {
  // Hard rejects
  if (projectFit === "rejected")
    return { disposition: "rejected", reason: "Project type not in service offering" };
  if (geo === "out_of_area")
    return { disposition: "rejected", reason: "Outside approved service area" };

  const hasInvalid = validations.some((v) => v.status === "invalid" && ["phone", "primaryPhone"].includes(v.field));
  if (hasInvalid)
    return { disposition: "rejected", reason: "Invalid required contact data" };

  // Duplicates
  const exactDup = duplicates.some((d) => d.type === "exact_phone" || d.type === "exact_email");
  if (exactDup)
    return { disposition: "duplicate_flagged", reason: `Exact match with ${duplicates[0].matchedName} (${duplicates[0].matchedLeadId})` };

  if (quality === "reject")
    return { disposition: "rejected", reason: "Lead quality score too low" };

  // Quarantine
  const softDup = duplicates.some((d) => d.type === "soft_name_zip" || d.type === "same_address");
  if (softDup)
    return { disposition: "quarantined", reason: `Possible duplicate: ${duplicates[0].matchedName}` };

  if (quality === "low")
    return { disposition: "quarantined", reason: "Low quality score — needs review" };

  // Warnings
  const hasSuspicious = validations.some((v) => v.status === "suspicious");
  const hasMissing = validations.some((v) => v.status === "missing" && v.field !== "email");
  if (hasSuspicious || hasMissing || projectFit === "flagged" || geo === "unknown")
    return { disposition: "approved_with_warning", reason: "Approved with data quality warnings" };

  return { disposition: "approved", reason: "All validations passed" };
}

// ─── Full Process Pipeline ───────────────────────────────────────────────────

export function processIncomingLead(
  rawPayload: Record<string, any>,
  source: IntakeSource,
  existingLeads: Lead[],
  config: IntegrityConfig = DEFAULT_INTEGRITY_CONFIG
): IntakeRecord {
  const now = new Date().toISOString();

  // Normalize
  const normalizedData = {
    primaryName: normalizeName(rawPayload.primaryName || rawPayload.name || ""),
    secondaryName: normalizeName(rawPayload.secondaryName || ""),
    primaryPhone: normalizePhone(rawPayload.primaryPhone || rawPayload.phone || ""),
    secondaryPhone: normalizePhone(rawPayload.secondaryPhone || ""),
    primaryEmail: normalizeEmail(rawPayload.primaryEmail || rawPayload.email || ""),
    secondaryEmail: normalizeEmail(rawPayload.secondaryEmail || ""),
    address: (rawPayload.address || "").trim(),
    city: normalizeName(rawPayload.city || ""),
    state: (rawPayload.state || "FL").trim().toUpperCase(),
    zip: normalizeZip(rawPayload.zip || ""),
    projectType: (rawPayload.projectType || "").trim(),
    leadSource: (rawPayload.leadSource || rawPayload.source || "").trim(),
  };

  // Validate
  const validations: ValidationResult[] = [
    ...validateRequiredFields(normalizedData, config.requiredFields),
    validatePhone(normalizedData.primaryPhone),
    validateEmail(normalizedData.primaryEmail),
  ];

  // Dedup
  const duplicates = detectDuplicates(normalizedData, existingLeads, config);

  // Geo
  const geoResult = checkGeo(normalizedData.zip, normalizedData.city, config);

  // Project fit
  const projectFitResult = checkProjectFit(normalizedData.projectType, config);

  // Score
  const { quality, total, breakdown } = scoreIntake(
    validations, duplicates, geoResult, projectFitResult,
    normalizedData.leadSource, config
  );

  // Decision
  const { disposition, reason } = makeDecision(
    validations, duplicates, geoResult, projectFitResult, quality
  );

  const record: IntakeRecord = {
    id: `INT-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    rawPayload,
    normalizedData,
    source,
    receivedAt: now,
    processedAt: now,
    validationResults: validations,
    duplicateMatches: duplicates,
    geoResult,
    projectFitResult,
    qualityScore: quality,
    scoreBreakdown: breakdown,
    totalScore: total,
    disposition,
    dispositionReason: reason,
    deliveredToPipeline: disposition === "approved" || disposition === "approved_with_warning",
  };

  return record;
}
